import { Ref, ref } from 'vue'
import { storeToRefs } from 'pinia'
import {useMainStore, useWidgetStore} from '@/store'
import { PPTElement } from '@/types/slides'

export default (elementList: Ref<PPTElement[]>, viewportRef: Ref<HTMLElement | undefined>) => {
  const mainStore = useMainStore()
  const widgetStore = useWidgetStore()

  const { canvasScale, hiddenElementIdList } = storeToRefs(mainStore)

  const rawMouseSelection = ref({ x1: 0, y1: 0, x2: 0, y2: 0 })
  const elementsWithPosition = ref([])

  const mouseSelectionVisible = ref(false)
  const mouseSelectionQuadrant = ref(1)
  const mouseSelection = ref({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  })

  document.onkeydown = e => {
    if (e.key === 'Control') {
      widgetStore.setMultiSelect(true)
    }
  }

  document.onkeyup = e => {
    if (e.key === 'Control') {
      widgetStore.setMultiSelect(false)
    }
  }

  // Update mouse frame selection range
  const updateMouseSelection = (e: MouseEvent) => {
    if (!viewportRef.value) return

    let isMouseDown = true
    const viewportRect = viewportRef.value.getBoundingClientRect()

    const minSelectionRange = 5

    const startPageX = e.pageX
    const startPageY = e.pageY

    rawMouseSelection.value = {
      x1: e.pageX,
      y1: e.pageY,
      x2: 0,
      y2: 0
    }

    const left = (startPageX - viewportRect.x) / canvasScale.value
    const top = (startPageY - viewportRect.y) / canvasScale.value

    // Determine the starting position of the box selection and other default value initialization
    mouseSelection.value = {
      top: top,
      left: left,
      width: 0,
      height: 0,
    }
    mouseSelectionVisible.value = false
    mouseSelectionQuadrant.value = 4

    document.onmousedown = e => {
      rawMouseSelection.value = {
        ...rawMouseSelection.value,
        x1: e.pageX,
        y1: e.pageY
      }

      elementsWithPosition.value = createElementListWithPosition()
    }

    document.onmousemove = e => {
      if (!isMouseDown) return

      const currentPageX = e.pageX
      const currentPageY = e.pageY

      const offsetWidth = (currentPageX - startPageX) / canvasScale.value
      const offsetHeight = (currentPageY - startPageY) / canvasScale.value

      const width = Math.abs(offsetWidth)
      const height = Math.abs(offsetHeight)

      if ( width < minSelectionRange || height < minSelectionRange ) return

      // Calculate the direction of the mouse selection (move)
      // Distinguish according to the position of the four quadrants, such as the fourth quadrant in the lower right corner
      let quadrant = 0
      if ( offsetWidth > 0 && offsetHeight > 0 ) quadrant = 4
      else if ( offsetWidth < 0 && offsetHeight < 0 ) quadrant = 1
      else if ( offsetWidth > 0 && offsetHeight < 0 ) quadrant = 2
      else if ( offsetWidth < 0 && offsetHeight > 0 ) quadrant = 3

      // Update box selection range
      mouseSelection.value = {
        ...mouseSelection.value,
        width: width,
        height: height,
      }

      rawMouseSelection.value = {
        ...rawMouseSelection.value,
        x2: e.pageX,
        y2: e.pageY
      }

      mouseSelectionVisible.value = true
      mouseSelectionQuadrant.value = quadrant

      rawMouseSelection.value = {
        ...rawMouseSelection.value,
        x2: e.pageX,
        y2: e.pageY
      }

      const rawValue = rawMouseSelection.value
      const rawTop = rawValue.y1 < rawValue.y2 ? rawValue.y1: rawValue.y2
      const rawBottom = rawValue.y1 < rawValue.y2 ? rawValue.y2: rawValue.y1
      const rawLeft = rawValue.x1 < rawValue.x2 ? rawValue.x1: rawValue.x2
      const rawRight = rawValue.x1 < rawValue.x2 ? rawValue.x2: rawValue.x1

      const selectedWidgets = []

      for(const elementWithPosition of elementsWithPosition.value) {
        const { element, rect } = elementWithPosition
        const notOverlapX = rect.x > rawRight || rect.x + rect.width < rawLeft
        const notOverlapY = rect.y > rawBottom || rect.y + rect.height < rawTop
        const isInclude = !notOverlapX && !notOverlapY

        if (isInclude) {
          selectedWidgets.push(element)
        }
      }

      widgetStore.setActiveWidgets(selectedWidgets)

    }

    document.onmouseup = e => {
      document.onmousemove = null
      document.onmouseup = null
      isMouseDown = false
      mouseSelectionVisible.value = false
    }
  }

  const createElementListWithPosition = () => {

    const elements = []

    for (let i = 0; i < elementList.value.length; i++) {
      const element = elementList.value[i]

      if (element.type !== 'slidelayout') {
        continue;
      }

      for (const row of element.layout.children) {
        for (const column of row.children) {
          if (column.dragDropElement.widgets) {
            for (const widget of column.dragDropElement.widgets) {
              for (const child of widget.children) {
                const div = document.getElementById(child.id)

                if (div) {
                  const rect = div.getBoundingClientRect();
                  if (rect.x > 0 || rect.y > 0) {
                    elements.push({
                      element: child,
                      rect: rect
                    })
                  }
                }
              }
            }
          }
        }
      }
    }

    return elements
  }


  return {
    mouseSelection,
    mouseSelectionVisible,
    mouseSelectionQuadrant,
    updateMouseSelection,
  }
}
